import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Chip,
  CircularProgress, Alert, LinearProgress, Divider,
  FormControl, InputLabel, Select, MenuItem, Avatar, Snackbar, Tooltip, FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  WarningAmber as WarningAmberIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import workOrderDetailService from '../../services/workOrderDetailService';
import workOrderService from '../../services/workOrderService';
import api from '../../config/axiosConfig';
import { FIELD_LIMITS } from '../../constants/fieldLimits';

const PURPLE_MAIN = '#9c27b0';
const PURPLE_DARK = '#7b1fa2';
const PURPLE_DEEP = '#6a1b9a';
const FIELD_STYLES = {
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': { borderColor: PURPLE_MAIN },
    '&.Mui-focused fieldset': { borderColor: PURPLE_MAIN },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: PURPLE_MAIN },
};
// El nivel de riesgo ya NO se captura manualmente por ente -- se hereda del/los servicio(s)
// contratados (ver ServiceCatalog.riskLevelId / WorkOrderDetailService.syncRiskLevelFromServices
// en el backend, recalculado en cada sync). Este mapa es solo para mostrarlo de forma legible.
const RISK_LEVEL_INFO = {
  1: { label: 'Bajo', bg: '#e8f5e9', color: '#2e7d32' },
  2: { label: 'Medio', bg: '#fff3e0', color: '#e65100' },
  3: { label: 'Alto', bg: '#ffebee', color: '#c62828' },
};

const WorkOrderDetailManager = ({ workOrder, onUpdate, onDataChanged, highlightDetail, onHighlightConsumed }) => {
  const [details, setDetails]                 = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [saving, setSaving]                   = useState(null);
  const [error, setError]                     = useState(null);
  // Campos faltantes por ente al intentar "Guardar todos": { [detailId]: ['scheduledDate', ...] }
  const [missingFields, setMissingFields]     = useState({});
  const [success, setSuccess]                 = useState(null);
  const [sharedDate, setSharedDate]                     = useState('');
  const [sharedEstimatedCompletionDate, setSharedEstimatedCompletionDate] = useState('');
  const [sharedTimeStart, setSharedTimeStart]           = useState('');
  const [sharedTimeEnd, setSharedTimeEnd]               = useState('');
  const [sharedAssignedUserId, setSharedAssignedUserId] = useState('');
  const [localEdits, setLocalEdits]                     = useState({});
  // Capacitaciones requeridas por ente (ver WorkOrderDetail.trainingRequirements) -- clave
  // `${detailId}_${serviceCatalogId}` porque un ente puede tener varios paquetes que la exigen.
  const [trainingEdits, setTrainingEdits]               = useState({});
  // Valores de capacitación tal como se cargaron, para saber cuáles cambiaron y deben guardarse
  // con "Guardar todos" (antes cada una tenía su propio botón "Programar" y se perdía si no se presionaba)
  const trainingOriginal                                = useRef({});
  const [trainingMissing, setTrainingMissing]           = useState({});
  const [operativeUsers, setOperativeUsers]             = useState([]);
  const [instructors, setInstructors]                   = useState([]);
  const [conflicts, setConflicts]                       = useState([]);
  const [conflictSnack, setConflictSnack]               = useState(false);
  const conflictRef                                     = useRef(null);

  const loadDetails = useCallback(async () => {
    if (!workOrder?.id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await workOrderDetailService.syncDetails(workOrder.id);
      setDetails(data);
      const firstScheduled = data.find((d) => d.scheduledDate || d.estimatedCompletionDate);
      setSharedDate(firstScheduled?.scheduledDate || '');
      setSharedEstimatedCompletionDate(firstScheduled?.estimatedCompletionDate || '');
      setSharedTimeStart(firstScheduled?.scheduledTimeStart || '');
      setSharedTimeEnd(firstScheduled?.scheduledTimeEnd || '');
      const edits = {};
      data.forEach((d) => {
        edits[d.id] = {
          scheduledDate:      d.scheduledDate      || '',
          estimatedCompletionDate: d.estimatedCompletionDate || '',
          scheduledTimeStart: d.scheduledTimeStart || '',
          scheduledTimeEnd:   d.scheduledTimeEnd   || '',
          assignedUserId:     d.assignedUserId     || '',
          inspectorContact:   d.quotationInspectorContact || '',
        };
      });
      setLocalEdits(edits);

      const tEdits = {};
      data.forEach((d) => {
        (d.trainingRequirements || []).forEach((req) => {
          const key = `${d.id}_${req.serviceCatalogId}`;
          tEdits[key] = {
            trainingId:     req.trainingId || null,
            trainingTopic:  req.trainingTopic || '',
            trainingDate:   req.trainingDate || '',
            startTime:      req.startTime || '',
            endTime:        req.endTime || '',
            tutorName:      req.tutorName || '',
            durationHours:  req.durationHours != null ? String(req.durationHours) : '',
            notes:          req.notes || '',
          };
        });
      });
      setTrainingEdits(tEdits);
      trainingOriginal.current = tEdits;
      setTrainingMissing({});
    } catch {
      setError('Error al cargar los entes de la orden');
    } finally {
      setLoading(false);
    }
  }, [workOrder]);

  const loadOperativeUsers = useCallback(async () => {
    try {
      // /users es solo ADMIN; esta pantalla la usan también Coordinador y Supervisor
      const response = await api.get('/users/executives');
      setOperativeUsers(Array.isArray(response.data) ? response.data : []);
    } catch { /* silencioso */ }
  }, []);

  const loadInstructors = useCallback(async () => {
    try {
      const response = await api.get('/instructors');
      setInstructors(response.data);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    loadDetails();
    loadOperativeUsers();
    loadInstructors();
  }, [loadDetails, loadOperativeUsers, loadInstructors]);

  // Si venimos de Validación de Campos (Programas.jsx) con un campo específico a resaltar (hoy
  // solo Nivel de Riesgo), buscamos el ente correspondiente por entityType/branchId y lo
  // desplazamos a la vista -- mismo patrón que CatalogoEmpresas.jsx/GestionSucursales.jsx.
  useEffect(() => {
    if (!highlightDetail || details.length === 0) return;
    const detail = details.find((d) => (
      highlightDetail.entityType === 'SUCURSAL'
        ? d.entityType === 'SUCURSAL' && String(d.branchId) === String(highlightDetail.branchId)
        : d.entityType === 'MATRIZ'
    ));
    if (detail) {
      const timer = setTimeout(() => {
        const el = document.getElementById(`field-${highlightDetail.fieldKey}-${detail.id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const target = el.closest('.MuiFormControl-root') || el.closest('.MuiPaper-root') || el;
          target.style.transition = 'box-shadow 0.3s ease';
          target.style.boxShadow = '0 0 0 3px #ff9800';
          target.style.borderRadius = target.style.borderRadius || '8px';
          setTimeout(() => { target.style.boxShadow = ''; }, 2500);
        }
      }, 400);
      onHighlightConsumed?.();
      return () => clearTimeout(timer);
    }
  }, [details, highlightDetail, onHighlightConsumed]);

  const handleFieldChange = (detailId, field, value) => {
    setLocalEdits((prev) => {
      const current = prev[detailId] || {};
      const updated = { ...current, [field]: value };

      const start = field === 'scheduledDate' ? value : updated.scheduledDate;
      const end   = field === 'estimatedCompletionDate' ? value : updated.estimatedCompletionDate;
      if (start && end && end < start) {
        setError('La fecha estimada de fin no puede ser anterior a la fecha de inicio.');
        return prev;
      }

      return { ...prev, [detailId]: updated };
    });
    if (value) {
      setMissingFields((prev) => {
        // Al corregir un campo se quita su marca, y la de "fin antes del inicio" si toca alguna hora
        const cleared = [field, `${field}Order`, ...(field === 'scheduledTimeStart' ? ['scheduledTimeEndOrder'] : [])];
        if (!prev[detailId] || !prev[detailId].some((f) => cleared.includes(f))) return prev;
        const remaining = prev[detailId].filter((f) => !cleared.includes(f));
        const next = { ...prev };
        if (remaining.length) next[detailId] = remaining; else delete next[detailId];
        return next;
      });
    }
  };


  const handleTrainingFieldChange = (key, field, value) => {
    setTrainingEdits((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
    if (value) {
      setTrainingMissing((prev) => {
        const cleared = [field, `${field}Order`, ...(field === 'startTime' ? ['endTimeOrder'] : [])];
        if (!prev[key] || !prev[key].some((f) => cleared.includes(f))) return prev;
        const remaining = prev[key].filter((f) => !cleared.includes(f));
        const next = { ...prev };
        if (remaining.length) next[key] = remaining; else delete next[key];
        return next;
      });
    }
  };

  const TRAINING_FIELDS = ['trainingDate', 'startTime', 'endTime', 'tutorName'];
  const trainingChanged = (key) => {
    const current = trainingEdits[key] || {};
    const original = trainingOriginal.current[key] || {};
    return TRAINING_FIELDS.some((f) => (current[f] || '') !== (original[f] || ''));
  };
  const pendingTrainingItems = details.flatMap((d) => (d.trainingRequirements || [])
    .map((req) => ({ detail: d, req, key: `${d.id}_${req.serviceCatalogId}` })))
    .filter((t) => t.detail.status !== 'COMPLETADA' && t.detail.status !== 'CANCELADA' && trainingChanged(t.key));

  const saveTraining = async ({ detail, req, key }) => {
    const edit = trainingEdits[key] || {};
    const payload = {
      workOrderId: workOrder.id,
      branchId: detail.branchId,
      serviceCatalogId: req.serviceCatalogId,
      trainingTopic: (edit.trainingTopic || '').trim(),
      trainingDate: edit.trainingDate,
      startTime: edit.startTime || null,
      endTime: edit.endTime || null,
      tutorName: (edit.tutorName || '').trim(),
      durationHours: edit.durationHours ? parseFloat(edit.durationHours) : null,
      notes: edit.notes || null,
    };
    return edit.trainingId
      ? api.put(`/trainings/${edit.trainingId}`, payload)
      : api.post('/trainings', payload);
  };


  const handleApplyDateToAll = () => {
    if (!sharedDate && !sharedEstimatedCompletionDate && !sharedAssignedUserId) return;
    if (sharedDate && sharedEstimatedCompletionDate && sharedEstimatedCompletionDate < sharedDate) {
      setError('La fecha estimada de fin no puede ser anterior a la fecha de inicio.');
      return;
    }
    setLocalEdits((prev) => {
      const next = { ...prev };
      details.forEach((d) => {
        if (d.status !== 'COMPLETADA' && d.status !== 'CANCELADA') {
          next[d.id] = {
            ...next[d.id],
            ...(sharedDate && { scheduledDate: sharedDate }),
            ...(sharedEstimatedCompletionDate && { estimatedCompletionDate: sharedEstimatedCompletionDate }),
            ...(sharedDate && sharedTimeStart && { scheduledTimeStart: sharedTimeStart }),
            ...(sharedDate && sharedTimeEnd   && { scheduledTimeEnd:   sharedTimeEnd   }),
            ...(sharedAssignedUserId && { assignedUserId: sharedAssignedUserId }),
          };
        }
      });
      return next;
    });
  };

  const detectLocalConflicts = (pendingDetails) => {
    const found = [];
    const groups = {};
    pendingDetails.forEach((d) => {
      const edit = localEdits[d.id];
      const userId = edit?.assignedUserId;
      if (!userId || !edit?.scheduledDate || !edit?.scheduledTimeStart) return;
      const key = `${userId}_${edit.scheduledDate}_${edit.scheduledTimeStart}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push({ detail: d, edit });
    });
    Object.values(groups).forEach((group) => {
      if (group.length < 2) return;
      const first = group[0];
      const user = operativeUsers.find((u) => u.id === parseInt(first.edit.assignedUserId));
      const name = user ? `${user.firstName} ${user.firstLastName}` : 'El ejecutivo asignado';
      const entities = group.map((x) => `"${x.detail.entityName}"`).join(', ');
      found.push(
        `${name} tiene programación duplicada (${first.edit.scheduledDate} ${first.edit.scheduledTimeStart}) en los entes: ${entities}`
      );
    });
    return found;
  };

  const hasChanges = (detail) => {
    const edit = localEdits[detail.id];
    if (!edit) return false;
    return (
      (edit.scheduledDate      || '') !== (detail.scheduledDate      || '') ||
      (edit.estimatedCompletionDate || '') !== (detail.estimatedCompletionDate || '') ||
      (edit.scheduledTimeStart || '') !== (detail.scheduledTimeStart || '') ||
      (edit.scheduledTimeEnd   || '') !== (detail.scheduledTimeEnd   || '') ||
      String(edit.assignedUserId || '') !== String(detail.assignedUserId || '') ||
      (edit.inspectorContact || '').trim() !== (detail.quotationInspectorContact || '').trim()
    );
  };

  const handleSaveAll = async () => {
    const editable = details.filter(
      (d) => d.status !== 'COMPLETADA' && d.status !== 'CANCELADA'
    );
    const pending = editable.filter((d) => hasChanges(d));
    const pendingTrainings = pendingTrainingItems;
    if (pending.length === 0 && pendingTrainings.length === 0) { setError('No hay cambios pendientes por guardar'); return; }

    // Todos los campos son obligatorios para todos los entes editables (misma regla que el backend,
    // WorkOrderDetailService.validateSchedule): fecha, fecha estimada de fin, hora de inicio y de fin
    // (fin después de inicio) y ejecutivo. Se marcan en rojo y se lleva al usuario al primero.
    const missingByDetail = {};
    editable.forEach((d) => {
      const edit = localEdits[d.id] || {};
      const missing = [];
      const start = edit.scheduledTimeStart || d.scheduledTimeStart;
      const end = edit.scheduledTimeEnd || d.scheduledTimeEnd;
      if (!(edit.scheduledDate || d.scheduledDate)) missing.push('scheduledDate');
      if (!(edit.estimatedCompletionDate || d.estimatedCompletionDate)) missing.push('estimatedCompletionDate');
      if (!start) missing.push('scheduledTimeStart');
      if (!end) missing.push('scheduledTimeEnd');
      else if (start && end <= start) missing.push('scheduledTimeEndOrder');
      if (!(edit.assignedUserId || d.assignedUserId)) missing.push('assignedUserId');
      if (missing.length) missingByDetail[d.id] = missing;
    });
    const incomplete = editable.filter((d) => missingByDetail[d.id]);
    setMissingFields(missingByDetail);
    if (incomplete.length > 0) {
      setError(
        `Todos los campos son obligatorios en todos los entes (fecha, fecha estimada de finalización, hora de inicio, hora de fin posterior al inicio y ejecutivo). Revisa: ${
          incomplete.map((d) => d.entityName).join(', ')
        }`
      );
      setTimeout(() => document.getElementById(`detail-card-${incomplete[0].id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }

    // Capacitación: obligatoria en todos los entes editables que la tengan en su paquete (misma regla
    // que el backend, TrainingService.validate): fecha, hora de inicio y de fin (fin después de
    // inicio) y tutor. Tema y duración vienen del paquete.
    const allTrainingItems = editable.flatMap((d) => (d.trainingRequirements || [])
      .map((req) => ({ detail: d, req, key: `${d.id}_${req.serviceCatalogId}` })));
    const trainingMissingByKey = {};
    allTrainingItems.forEach(({ key }) => {
      const edit = trainingEdits[key] || {};
      const missing = [];
      if (!edit.trainingDate) missing.push('trainingDate');
      if (!edit.startTime) missing.push('startTime');
      if (!edit.endTime) missing.push('endTime');
      else if (edit.startTime && edit.endTime <= edit.startTime) missing.push('endTimeOrder');
      if (!(edit.tutorName || '').trim()) missing.push('tutorName');
      if (missing.length) trainingMissingByKey[key] = missing;
    });
    setTrainingMissing(trainingMissingByKey);
    const incompleteTrainings = allTrainingItems.filter((t) => trainingMissingByKey[t.key]);
    if (incompleteTrainings.length > 0) {
      setError(`Capacitación incompleta en: ${incompleteTrainings.map((t) => t.detail.entityName).join(', ')}. `
        + 'Todos sus campos son obligatorios: fecha, hora de inicio, hora de fin (posterior al inicio) y tutor.');
      setTimeout(() => document.getElementById(`training-${incompleteTrainings[0].key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }

    const found = detectLocalConflicts(pending);
    if (found.length > 0) {
      setConflicts(found);
      setConflictSnack(true);
      setTimeout(() => conflictRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      return;
    }
    setConflicts([]);
    setConflictSnack(false);
    try {
      setSaving('all');
      setError(null);
      for (const d of pending) {
        const edit = localEdits[d.id];
        const selectedUser = operativeUsers.find((u) => u.id === parseInt(edit.assignedUserId));
        await workOrderDetailService.scheduleEntity(d.id, {
          scheduledDate:      edit.scheduledDate,
          estimatedCompletionDate: edit.estimatedCompletionDate || null,
          scheduledTimeStart: edit.scheduledTimeStart,
          scheduledTimeEnd:   edit.scheduledTimeEnd || null,
          assignedUserId:     edit.assignedUserId   || null,
          assignedInspector:  selectedUser ? `${selectedUser.firstName} ${selectedUser.firstLastName}` : null,
          inspectorContact:   (edit.inspectorContact || '').trim(),
        });
      }
      if (pending.length > 0) {
        await workOrderService.updateStatus(workOrder.id, 'ASIGNADA');
      }
      for (const t of pendingTrainings) {
        await saveTraining(t);
      }
      const parts = [];
      if (pending.length > 0) parts.push(`${pending.length} inspección(es)`);
      if (pendingTrainings.length > 0) parts.push(`${pendingTrainings.length} capacitación(es)`);
      setSuccess(`✅ Guardado: ${parts.join(' y ')}.${pending.length > 0 ? ' Orden marcada como ASIGNADA. Cerrando...' : ''}`);
      loadDetails();
      if (onDataChanged) onDataChanged();
      if (onUpdate && pending.length > 0) setTimeout(() => onUpdate(), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar los horarios');
    } finally {
      setSaving(null);
    }
  };

  const STATUS_LABEL = {
    PENDIENTE: 'Sin programar', PROGRAMADA: 'Programado',
    EN_PROGRESO: 'En progreso', COMPLETADA: 'Completado', CANCELADA: 'Cancelado',
  };
  const STATUS_COLOR = {
    PENDIENTE: 'default', PROGRAMADA: 'info',
    EN_PROGRESO: 'warning', COMPLETADA: 'success', CANCELADA: 'error',
  };

  const editableDetails = details.filter(
    (d) => d.status !== 'COMPLETADA' && d.status !== 'CANCELADA'
  );
  const pendingDetails = editableDetails.filter((d) => hasChanges(d));
  const detectedConflicts = detectLocalConflicts(pendingDetails);
  const hasDuplicateConflicts = detectedConflicts.length > 0;

  const scheduledCount = details.filter(
    (d) => ['PROGRAMADA', 'EN_PROGRESO', 'COMPLETADA'].includes(d.status)
  ).length;
  const progressPct    = details.length > 0 ? (scheduledCount / details.length) * 100 : 0;
  const pendingInspections = pendingDetails.length;
  const pendingChanges = pendingInspections + pendingTrainingItems.length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
        <CircularProgress sx={{ color: PURPLE_MAIN }} size={48} />
        <Typography variant="body2" color="text.secondary">Cargando entes de la orden...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error   && <Alert severity="error"   onClose={() => setError(null)}   sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

      {/* ── Snackbar conflicto (siempre visible) ── */}
      <Snackbar
        open={conflictSnack}
        onClose={() => setConflictSnack(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={6000}
      >
        <Alert severity="warning" onClose={() => setConflictSnack(false)} sx={{ width: '100%', fontWeight: 'bold' }}>
          Programación duplicada — revisa el detalle arriba antes de guardar
        </Alert>
      </Snackbar>

      {/* ── Conflictos detectados ── */}
      {(conflicts.length > 0 || hasDuplicateConflicts) && (
        <Paper ref={conflictRef} elevation={0} sx={{
          mb: 3, p: 2.5, borderRadius: 2,
          border: '2px solid #f44336',
          background: 'linear-gradient(135deg, #fff5f5 0%, #ffebee 100%)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
            <WarningAmberIcon sx={{ color: '#f44336', mt: 0.25, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#c62828', mb: 0.5 }}>
                ⚠️ Programación duplicada detectada
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                No se permiten duplicados con la misma fecha, hora de inicio y ejecutivo asignado. Corrige los datos para habilitar el guardado.
              </Typography>
              {(hasDuplicateConflicts ? detectedConflicts : conflicts).map((msg, i) => (
                <Typography key={i} variant="body2" sx={{
                  color: '#b71c1c', fontSize: 12, mb: 0.5,
                  pl: 1, borderLeft: '3px solid #f44336',
                }}>
                  {msg}
                </Typography>
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button size="small" variant="outlined"
              onClick={() => setConflicts([])}
              sx={{
                textTransform: 'none', borderColor: '#f44336', color: '#c62828', borderRadius: 2,
                '&:hover': { borderColor: '#c62828', backgroundColor: '#ffebee' },
              }}>
              Revisar y corregir
            </Button>
          </Box>
        </Paper>
      )}

      {/* ── Progreso ── */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        border: `1px solid ${PURPLE_MAIN}30`,
        background: `linear-gradient(135deg, ${PURPLE_MAIN}08 0%, ${PURPLE_DARK}05 100%)`,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon sx={{ color: PURPLE_MAIN, fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ color: PURPLE_DARK, fontWeight: 'bold' }}>
              Progreso de programación
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {scheduledCount} de {details.length} entes programados
            </Typography>
            {pendingChanges > 0 && (
              <Chip label={`${pendingChanges} sin guardar`} size="small"
                sx={{ backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ff9800', fontWeight: 'bold', fontSize: 11 }} />
            )}
          </Box>
        </Box>
        <LinearProgress variant="determinate" value={progressPct} sx={{
          height: 8, borderRadius: 4, backgroundColor: `${PURPLE_MAIN}20`,
          '& .MuiLinearProgress-bar': {
            background: `linear-gradient(90deg, ${PURPLE_MAIN} 0%, ${PURPLE_DARK} 100%)`, borderRadius: 4,
          },
        }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: PURPLE_DARK, fontWeight: 'bold' }}>
            {Math.round(progressPct)}%
          </Typography>
        </Box>
      </Paper>

      {/* ── Tarjetas por ente ── */}
      {details.length === 0 ? (
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: 2, border: '2px dashed #e0e0e0', backgroundColor: '#fafafa' }}>
          <ScheduleIcon sx={{ fontSize: 56, color: '#bdbdbd', mb: 1.5 }} />
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            No hay entes registrados para esta orden
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Los entes se crean automáticamente al aceptar una cotización con sucursales.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <strong>Inspección: obligatoria</strong> para todos los entes (fecha, fecha estimada fin, hora y ejecutivo).
            {' '}<strong>Capacitación: opcional</strong>, puedes programarla ahora o después.
            {' '}Todo se guarda con el botón <strong>"Guardar todos"</strong> al final.
          </Alert>
          {details.map((detail, index) => {
            const edit         = localEdits[detail.id] || {};
            const isEditable   = detail.status !== 'COMPLETADA' && detail.status !== 'CANCELADA';
            const rowChanged   = hasChanges(detail);
            const isMatriz     = detail.entityType === 'MATRIZ';
            const assignedUser = operativeUsers.find((u) => u.id === detail.assignedUserId);
            const missing      = missingFields[detail.id] || [];

            return (
              <Paper key={detail.id} id={`detail-card-${detail.id}`} elevation={rowChanged ? 3 : 1} sx={{
                borderRadius: 2, overflow: 'hidden',
                opacity: detail.status === 'CANCELADA' ? 0.55 : 1,
                border: missing.length ? '2px solid #d32f2f' : rowChanged ? '2px solid #ff9800' : '1px solid #e0e0e0',
                transition: 'box-shadow 0.2s, border 0.2s',
                '&:hover': { boxShadow: 3 },
              }}>
                <Box sx={{ display: 'flex' }}>
                  <Box sx={{
                    width: 5, flexShrink: 0,
                    background: isMatriz
                      ? `linear-gradient(180deg, ${PURPLE_MAIN} 0%, ${PURPLE_DEEP} 100%)`
                      : 'linear-gradient(180deg, #1976d2 0%, #1565c0 100%)',
                  }} />

                  <Box sx={{ flex: 1, p: 2 }}>
                    {/* Encabezado */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: '50%', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0,
                          background: isMatriz
                            ? `linear-gradient(135deg, ${PURPLE_MAIN} 0%, ${PURPLE_DARK} 100%)`
                            : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        }}>
                          {isMatriz ? <BusinessIcon sx={{ fontSize: 16 }} /> : <LocationOnIcon sx={{ fontSize: 16 }} />}
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#212121' }}>
                              {index + 1}. {detail.entityName}
                            </Typography>
                            <Chip label={isMatriz ? 'Matriz' : 'Sucursal'} size="small" sx={{
                              height: 20, fontSize: 10, fontWeight: 'bold',
                              backgroundColor: isMatriz ? `${PURPLE_MAIN}15` : '#e3f2fd',
                              color: isMatriz ? PURPLE_DARK : '#1565c0',
                              border: `1px solid ${isMatriz ? PURPLE_MAIN : '#1976d2'}40`,
                            }} />
                          </Box>
                          {detail.scheduledDate && !rowChanged && (
                            <Typography variant="caption" color="text.secondary">
                              📅 {detail.scheduledDate}
                              {detail.scheduledTimeStart && ` · ${detail.scheduledTimeStart}`}
                              {detail.scheduledTimeEnd   && ` - ${detail.scheduledTimeEnd}`}
                            </Typography>
                          )}
                          {detail.estimatedCompletionDate && !rowChanged && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              ✅ Finalización estimada: {detail.estimatedCompletionDate}
                            </Typography>
                          )}
                          {assignedUser && (!isEditable || !rowChanged) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                              <Avatar sx={{ width: 16, height: 16, fontSize: 10, bgcolor: PURPLE_MAIN }}>
                                {assignedUser.firstName?.[0]}
                              </Avatar>
                              <Typography variant="caption" sx={{ color: PURPLE_DARK, fontWeight: 600 }}>
                                {assignedUser.firstName} {assignedUser.firstLastName}
                              </Typography>
                              {assignedUser.email && (
                                <Typography variant="caption" color="text.secondary">
                                  · {assignedUser.email}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <Chip
                        label={STATUS_LABEL[detail.status] || detail.status}
                        color={STATUS_COLOR[detail.status] || 'default'}
                        size="small"
                        icon={detail.status === 'COMPLETADA' ? <CheckCircleIcon style={{ fontSize: 14 }} /> : undefined}
                        sx={{ fontWeight: 'bold', fontSize: 11 }}
                      />
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {isEditable ? (
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <TextField label="Fecha" type="date" size="small"
                          error={missing.includes('scheduledDate')}
                          helperText={missing.includes('scheduledDate') ? 'Requerido' : ''}
                          value={edit.scheduledDate || ''}
                          onChange={(e) => handleFieldChange(detail.id, 'scheduledDate', e.target.value)}
                          InputLabelProps={{ shrink: true }} sx={{ minWidth: 160, ...FIELD_STYLES }} />
                        <TextField label="Fecha estimada fin" type="date" size="small"
                          error={missing.includes('estimatedCompletionDate')}
                          helperText={missing.includes('estimatedCompletionDate') ? 'Requerido' : ''}
                          value={edit.estimatedCompletionDate || ''}
                          onChange={(e) => handleFieldChange(detail.id, 'estimatedCompletionDate', e.target.value)}
                          InputLabelProps={{ shrink: true }} sx={{ minWidth: 190, ...FIELD_STYLES }} />
                        <TextField label="Hora inicio" type="time" size="small"
                          error={missing.includes('scheduledTimeStart')}
                          helperText={missing.includes('scheduledTimeStart') ? 'Requerido' : ''}
                          value={edit.scheduledTimeStart || ''}
                          onChange={(e) => handleFieldChange(detail.id, 'scheduledTimeStart', e.target.value)}
                          InputLabelProps={{ shrink: true }} sx={{ minWidth: 130, ...FIELD_STYLES }} />
                        <TextField label="Hora fin" type="time" size="small"
                          error={missing.includes('scheduledTimeEnd') || missing.includes('scheduledTimeEndOrder')}
                          helperText={missing.includes('scheduledTimeEnd') ? 'Requerido'
                            : missing.includes('scheduledTimeEndOrder') ? 'Debe ser posterior al inicio' : ''}
                          value={edit.scheduledTimeEnd || ''}
                          onChange={(e) => handleFieldChange(detail.id, 'scheduledTimeEnd', e.target.value)}
                          InputLabelProps={{ shrink: true }} sx={{ minWidth: 130, ...FIELD_STYLES }} />

                        {/* Ejecutivo de Operaciones por ente -- el campo cerrado muestra
                            "Nombre ApellidoPaterno" (no el correo, para no verse abarrotado);
                            el correo se ve como subtítulo de cada opción al desplegar la lista,
                            para distinguir homónimos. */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FormControl size="small" error={missing.includes('assignedUserId')} sx={{ minWidth: 210, ...FIELD_STYLES }}>
                            <InputLabel sx={{ '&.Mui-focused': { color: PURPLE_MAIN } }}>
                              Ejecutivo de Operaciones
                            </InputLabel>
                            <Select
                              label="Ejecutivo de Operaciones"
                              value={edit.assignedUserId || ''}
                              onChange={(e) => handleFieldChange(detail.id, 'assignedUserId', e.target.value)}
                              renderValue={(selected) => {
                                if (!selected) return <em>Sin asignar</em>;
                                const user = operativeUsers.find((u) => u.id === selected);
                                if (!user) return '';
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 20, height: 20, fontSize: 11, bgcolor: PURPLE_MAIN }}>
                                      {user.firstName?.[0]}
                                    </Avatar>
                                    {user.firstName} {user.firstLastName}
                                  </Box>
                                );
                              }}
                            >
                              <MenuItem value=""><em>Sin asignar</em></MenuItem>
                              {operativeUsers.map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: PURPLE_MAIN }}>
                                      {user.firstName?.[0]}
                                    </Avatar>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {user.firstName} {user.firstLastName}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {user.email}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                            {missing.includes('assignedUserId') && <FormHelperText>Requerido</FormHelperText>}
                          </FormControl>
                        </Box>

                        {/* Opcional aquí (obligatorio al Iniciar Programa): viene de lo que capturó el
                            cliente en perfil-riesgo; se guarda en el mismo ente y sale en el correo. */}
                        <TextField label="Persona que acompañará en el recorrido (opcional)" size="small"
                          helperText={detail.quotationInspectorContact ? 'Capturado por el cliente o en Iniciar Programa'
                            : 'Si aún no se sabe, se pide al Iniciar Programa'}
                          value={edit.inspectorContact || ''}
                          onChange={(e) => handleFieldChange(detail.id, 'inspectorContact', e.target.value)}
                          inputProps={{ maxLength: FIELD_LIMITS.inspectorContact }}
                          sx={{ minWidth: 280, flex: 1, ...FIELD_STYLES }} />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#212121' }}>
                            📅 {detail.scheduledDate || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            ✅ Estimada fin: {detail.estimatedCompletionDate || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {detail.scheduledTimeStart
                              ? `${detail.scheduledTimeStart}${detail.scheduledTimeEnd ? ' – ' + detail.scheduledTimeEnd : ''}`
                              : 'Sin horario definido'}
                          </Typography>
                          {assignedUser && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                              <PersonIcon sx={{ fontSize: 13, color: PURPLE_MAIN }} />
                              <Typography variant="caption" sx={{ color: PURPLE_DARK, fontWeight: 600 }}>
                                {assignedUser.firstName} {assignedUser.firstLastName}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Nivel de riesgo por ente -- de solo lectura, se hereda del servicio
                        contratado para este ente (ver ServiceCatalog.riskLevelId en Catálogo de
                        Servicios); ya no se captura manualmente aquí. */}
                    <Box id={`field-NIVEL_RIESGO-${detail.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Nivel de Riesgo:</Typography>
                      {detail.riskLevelId && RISK_LEVEL_INFO[detail.riskLevelId] ? (
                        <Chip
                          label={RISK_LEVEL_INFO[detail.riskLevelId].label}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: RISK_LEVEL_INFO[detail.riskLevelId].bg, color: RISK_LEVEL_INFO[detail.riskLevelId].color }}
                        />
                      ) : (
                        <Chip label="Sin definir" size="small" sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: '#757575' }} />
                      )}
                    </Box>

                    {/* ── Capacitaciones requeridas por paquete comprado ── */}
                    {(detail.trainingRequirements || []).length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        {detail.trainingRequirements.map((req) => {
                          const key = `${detail.id}_${req.serviceCatalogId}`;
                          const tEdit = trainingEdits[key] || {};
                          const isScheduled = !!tEdit.trainingId;
                          const isDirty = trainingChanged(key);
                          const tMissing = trainingMissing[key] || [];
                          return (
                            <Box key={key} id={`training-${key}`} sx={{
                              p: 1.5, mb: 1.5, borderRadius: 2,
                              border: tMissing.length ? '2px solid #d32f2f' : `1px dashed ${isDirty ? '#ff9800' : isScheduled ? '#4caf50' : '#0288d1'}`,
                              backgroundColor: isScheduled ? '#f1f8f1' : '#f0f9ff',
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isEditable ? 1 : 0.5 }}>
                                <SchoolIcon sx={{ fontSize: 18, color: isScheduled ? '#2e7d32' : '#0288d1' }} />
                                <Typography variant="subtitle2" sx={{ color: '#01579b', fontWeight: 700 }}>
                                  Capacitación: {req.serviceName}
                                </Typography>
                                <Chip
                                  label={isDirty ? 'Cambios sin guardar' : isScheduled ? 'Programada' : 'Sin programar'}
                                  size="small"
                                  color={isDirty ? 'warning' : isScheduled ? 'success' : 'default'}
                                  sx={{ height: 20, fontSize: 10, fontWeight: 'bold' }}
                                />
                              </Box>

                              {isEditable ? (
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                  <TextField label="Tema" size="small"
                                    value={tEdit.trainingTopic || ''}
                                    InputProps={{ readOnly: true }}
                                    title="El tema viene del paquete contratado en la cotización -- no es editable aquí"
                                    sx={{ minWidth: 220, ...FIELD_STYLES, '& .MuiInputBase-input': { backgroundColor: '#f5f5f5', cursor: 'default' } }} />
                                  <TextField label="Fecha" type="date" size="small"
                                    error={tMissing.includes('trainingDate')}
                                    helperText={tMissing.includes('trainingDate') ? 'Requerido' : ''}
                                    value={tEdit.trainingDate || ''}
                                    onChange={(e) => handleTrainingFieldChange(key, 'trainingDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }} sx={{ minWidth: 160, ...FIELD_STYLES }} />
                                  <TextField label="Hora inicio" type="time" size="small"
                                    error={tMissing.includes('startTime')}
                                    helperText={tMissing.includes('startTime') ? 'Requerido' : ''}
                                    value={tEdit.startTime || ''}
                                    onChange={(e) => handleTrainingFieldChange(key, 'startTime', e.target.value)}
                                    InputLabelProps={{ shrink: true }} sx={{ minWidth: 130, ...FIELD_STYLES }} />
                                  <TextField label="Hora fin" type="time" size="small"
                                    error={tMissing.includes('endTime') || tMissing.includes('endTimeOrder')}
                                    helperText={tMissing.includes('endTime') ? 'Requerido'
                                      : tMissing.includes('endTimeOrder') ? 'Debe ser posterior al inicio' : ''}
                                    value={tEdit.endTime || ''}
                                    onChange={(e) => handleTrainingFieldChange(key, 'endTime', e.target.value)}
                                    InputLabelProps={{ shrink: true }} sx={{ minWidth: 130, ...FIELD_STYLES }} />
                                  <FormControl size="small" error={tMissing.includes('tutorName')} sx={{ minWidth: 180, ...FIELD_STYLES }}>
                                    <InputLabel sx={{ '&.Mui-focused': { color: PURPLE_MAIN } }}>Tutor</InputLabel>
                                    <Select
                                      label="Tutor"
                                      value={tEdit.tutorName || ''}
                                      onChange={(e) => handleTrainingFieldChange(key, 'tutorName', e.target.value)}
                                    >
                                      <MenuItem value=""><em>Sin asignar</em></MenuItem>
                                      {instructors.map((instructor) => (
                                        <MenuItem key={instructor.id} value={instructor.name}>
                                          {instructor.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {tMissing.includes('tutorName') && <FormHelperText>Requerido</FormHelperText>}
                                  </FormControl>
                                  <TextField label="Duración (hrs)" type="number" size="small"
                                    value={tEdit.durationHours || ''}
                                    InputProps={{ readOnly: true }}
                                    title="La duración viene del paquete contratado en la cotización -- no es editable aquí"
                                    sx={{ minWidth: 130, ...FIELD_STYLES, '& .MuiInputBase-input': { backgroundColor: '#f5f5f5', cursor: 'default' } }} />
                                </Box>
                              ) : null}
                              {isEditable ? null : (
                                <Typography variant="caption" color="text.secondary">
                                  {isScheduled
                                    ? `${tEdit.trainingTopic} · ${tEdit.trainingDate}${tEdit.startTime ? ` ${tEdit.startTime}` : ''} · Tutor: ${tEdit.tutorName}`
                                    : 'Sin programar'}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* ── Guardar todos ── */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{
        p: 2, borderRadius: 2,
        backgroundColor: saving === 'all' ? `${PURPLE_MAIN}12` : pendingChanges > 0 ? '#fff3e0' : `${PURPLE_MAIN}08`,
        border: `1px solid ${saving === 'all' ? PURPLE_MAIN : pendingChanges > 0 ? '#ff9800' : PURPLE_MAIN + '40'}`,
        transition: 'all 0.3s',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: saving === 'all' ? PURPLE_DARK : pendingChanges > 0 ? '#e65100' : 'text.secondary', fontWeight: saving === 'all' ? 'bold' : 'normal' }}>
            {saving === 'all'
              ? 'Guardando la programación de todos los entes, no cierres esta ventana...'
              : pendingChanges > 0
                ? <>Sin guardar: {pendingInspections > 0 && <><strong>{pendingInspections}</strong> inspección(es)</>}
                    {pendingInspections > 0 && pendingTrainingItems.length > 0 && ' y '}
                    {pendingTrainingItems.length > 0 && <><strong>{pendingTrainingItems.length}</strong> capacitación(es)</>}
                    {hasDuplicateConflicts ? ' (hay duplicados por corregir)' : ''}</>
                : 'Sin cambios pendientes'}
          </Typography>
          <Tooltip
            title={pendingChanges === 0
              ? 'Llena los campos del panel superior y presiona "Aplicar a todos" para habilitar el guardado'
              : hasDuplicateConflicts
                ? 'Corrige los duplicados (misma fecha + hora inicio + ejecutivo) para habilitar el guardado'
                : ''}
            arrow
            placement="top"
          >
            <span>
              <Button variant="contained" onClick={handleSaveAll} disabled={saving === 'all' || pendingChanges === 0 || hasDuplicateConflicts}
                startIcon={saving === 'all' ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
                sx={{
                  textTransform: 'none', borderRadius: 2, px: 4, fontWeight: 'bold',
                  background: (saving === 'all' || (pendingChanges > 0 && !hasDuplicateConflicts))
                    ? `linear-gradient(135deg, ${PURPLE_MAIN} 0%, ${PURPLE_DARK} 100%)`
                    : undefined,
                  '&:hover': pendingChanges > 0 && !hasDuplicateConflicts
                    ? { background: `linear-gradient(135deg, ${PURPLE_DARK} 0%, ${PURPLE_DEEP} 100%)` }
                    : undefined,
                  // Al guardar, el botón sigue deshabilitado (para no permitir doble clic) pero debe
                  // verse "activo" (color, no gris) -- si no, se ve idéntico a simplemente
                  // deshabilitado y no se percibe que está procesando.
                  '&.Mui-disabled': saving === 'all'
                    ? { background: `linear-gradient(135deg, ${PURPLE_MAIN} 0%, ${PURPLE_DARK} 100%)`, color: 'white' }
                    : { background: '#e0e0e0', color: '#9e9e9e' },
                }}>
                {saving === 'all' ? 'Guardando...' : `Guardar todos${pendingChanges > 0 ? ` (${pendingChanges})` : ''}`}
              </Button>
            </span>
          </Tooltip>
        </Box>
        {Object.keys(missingFields).length > 0 && saving !== 'all' && (
          <Alert severity="error" variant="filled" sx={{ mt: 1.5, borderRadius: 2 }}>
            Faltan datos en: <strong>{details.filter((d) => missingFields[d.id]).map((d) => d.entityName).join(', ')}</strong>.
            {' '}Completa los campos marcados en rojo para guardar.
          </Alert>
        )}
        {saving === 'all' && (
          <LinearProgress sx={{
            mt: 1.5, height: 4, borderRadius: 2, backgroundColor: `${PURPLE_MAIN}20`,
            '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${PURPLE_MAIN} 0%, ${PURPLE_DARK} 100%)` },
          }} />
        )}
      </Box>
    </Box>
  );
};

export default WorkOrderDetailManager;
